import React, { useState, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import IndentDetails from "../IndentPage/ItemsPage/IndentDetails";
import ItemsTable from "./PEItemsTable";
import { useSelector, useDispatch } from "react-redux";
import IndentChangesTracker from "../IndentChangesTracker";
import { Loader2, AlertCircle, X } from "lucide-react";
import ActionBar from "../../../components/CRUDcomponents/Actionbar/ActionBar";
import DynamicTable from "../../../components/Table/DIndentTable2";
import { fetchIndentData, fetchDropdownOptions, filterIndents } from '../../../store/indentSlice';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

const LoadingOverlay = ({ message }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center space-y-4">
      <Loader2 className="animate-spin" size={32} />
      <span className="text-lg font-medium">{message}</span>
    </div>
  </div>
);

const ErrorAlert = ({ message }) => (
  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start space-x-2">
    <AlertCircle className="text-red-500 mt-0.5" size={20} />
    <span className="text-red-700">{message}</span>
  </div>
);

const LoadingScreen = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="bg-white p-6 rounded-lg shadow-xl flex items-center space-x-4">
      <Loader2 className="animate-spin" size={24} />
      <span className="text-lg">Loading...</span>
    </div>
  </div>
);

const useIndentData = (indentId, apiEndpoint) => {
  const [pageData, setPageData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!indentId) return;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [indentResponse, itemsResponse] = await Promise.all([
          axios.get(apiEndpoint),
          axios.get(`${API_BASE_URL}/viewTable/indentitems/${indentId}`)
        ]);

        const indentData = Array.isArray(indentResponse.data)
          ? indentResponse.data[0]
          : indentResponse.data;

        if (indentData) {
          setPageData({
            ...indentData,
            IndentItems: itemsResponse.data || []
          });
        }
      } catch (err) {
        setError(err.message);
        setPageData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [indentId, apiEndpoint]);

  return { pageData, isLoading, error };
};

const InvoicingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { role, id: enteredBy } = useSelector((state) => state.auth);
  const { indents, filteredIndents, dropdownData, indentLoading, indentError } = useSelector((state) => state.indent);
  const [row, setRow] = useState(null);
  const [items, setItems] = useState([]);
  const [isDraft, setIsDraft] = useState(true);
  const [isEditable, setIsEditable] = useState(true);
  const [categories, setCategories] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pageError, setPageError] = useState(null);
  const [pageData, setPageData] = useState(null);
  const currentUserId = useSelector((state) => state.auth.id);
  const [submitting, setSubmitting] = useState(false);
  const [changeLog, setChangeLog] = useState({
    addedItems: [],
    deletedItems: [],
    updatedItems: [],
  });
  const [storedChangeLogs, setStoredChangeLogs] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [allChanges, setAllChanges] = useState({
    added: [],
    deleted: [],
    updated: [],
  });
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    dispatch(fetchIndentData({ role, enteredBy }));
    dispatch(fetchDropdownOptions());
  }, [dispatch, role, enteredBy]);

  const getEditFields = [
    {
      name: "ProjectID",
      label: "Project",
      type: "select",
      options: dropdownData?.projects?.map((project) => ({
        value: project.id,
        label: project.name,
      })) || [],
      className: "w-full",
    },
    {
      name: "TypeID",
      label: "Type",
      type: "select",
      options: dropdownData?.types?.map((type) => ({
        value: type.id,
        label: type.name,
      })) || [],
      className: "w-full",
    },
  ];

  // Initialize row data from location state
  useEffect(() => {
    if (location.state?.row) {
      console.log('Setting row data:', location.state.row);
      setRow(location.state.row);
    } else {
      console.error('No row data found in location state');
      navigate("/peindent", {
        state: { message: "Invalid indent access. Redirecting to indent list." }
      });
    }
  }, [location.state, navigate]);

  const apiEndpoint = row?.IndentID ? `${API_BASE_URL}/l2page/indents/${row.IndentID}` : null;

  const { pageData: pageDataFromUseIndentData, isLoading: pageDataLoading, error: indentDataError } = useIndentData(
    row?.IndentID,
    apiEndpoint
  );

  const refreshPage = async () => {
    if (!row?.IndentID) return;
    
    setIsLoading(true);
    try {
      await fetchDraftItems();
      await fetchChangeLogs();
      setRefreshKey((prevKey) => prevKey + 1);
    } catch (error) {
      console.error("Error refreshing page:", error);
      setPageError("Failed to refresh page data");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDraftItems = async () => {
    if (!row?.IndentID) return;

    try {
      const response = await axios.get(
        `${API_BASE_URL}/viewTable/indentitems`,
        {
          params: { indentId: row.IndentID },
        }
      );

      const indentStatus = response.data.length > 0
        ? {
            l1: response.data[0].L1A || 0,
            l2: response.data[0].L2 || 0,
            po: response.data[0].PO || 0,
            ph: response.data[0].PH || 0,
            so: response.data[0].IR || 0,
          }
        : { l1: 0, l2: 0, po: 0, ph: 0, so: 0 };

      const draftItems = response.data.map((item) => ({
        itemID: item.ItemID,
        itemName: item.ItemName,
        quantity: item.Quantity,
        stock: item.Stock,
        categoryID: item.CategoryID,
        remarks: item.Description,
        promisedDate: item.PromisedDate,
        total: item.Quantity * item.Stock,
        accountingUnitID: item.AccountingUnitID,
      }));

      setItems(draftItems);
      setIsDraft(true);

      setRow(prev => ({
        ...prev,
        L1: indentStatus.l1,
        L2: indentStatus.l2,
        PO: indentStatus.po,
        PH: indentStatus.ph,
        IR: indentStatus.so,
      }));
    } catch (error) {
      console.error("Error fetching draft items:", error);
      setPageError("Failed to load draft items");
    }
  };

  const fetchChangeLogs = async () => {
    if (!row?.IndentID) return;

    try {
      const response = await axios.get(
        `${API_BASE_URL}/change_log/change-logs/${row.IndentID}`
      );
      setStoredChangeLogs(response.data);
    } catch (error) {
      console.error("Error fetching change logs:", error);
    }
  };

  useEffect(() => {
    if (row?.IndentID) {
      fetchDraftItems();
      fetchChangeLogs();
    }
  }, [row?.IndentID, refreshKey]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoryResponse = await axios.get(
          `${API_BASE_URL}/dropdown/mcatdrop`
        );
        setCategories(categoryResponse.data || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  const saveChangeLog = async (changeLogData) => {
    if (!row?.IndentID) return;

    try {
      await axios.post(`${API_BASE_URL}/change_log/change-logs`, {
        indentId: row.IndentID,
        userId: currentUserId,
        currentlevel: "Indent",
        ...changeLogData,
      });
    } catch (error) {
      console.error("Error saving change log:", error);
      setPageError("Failed to save change log");
    }
  };

  const handleItemUpdate = async (updateInfo) => {
    setIsLoading(true);
    try {
      if (updateInfo.type === "add") {
        await saveChangeLog({ type: "add", item: updateInfo.item });
        await refreshPage();
      } else if (updateInfo.type === "update" && updateInfo.items) {
        setItems(updateInfo.items);
        await saveChangeLog({
          type: "update",
          original: items.find(
            (item) => item.itemID === updateInfo.item.itemID
          ),
          updated: updateInfo.item,
        });
        await refreshPage();
      } else if (updateInfo.type === "delete") {
        await saveChangeLog({ type: "delete", item: updateInfo.item });
        await refreshPage();
      }
    } catch (error) {
      console.error("Error updating items:", error);
      setPageError("Failed to update items");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddItem = (newItem) => {
    if (!newItem.itemID || newItem.quantity <= 0 || newItem.stock < 0) {
      setPageError("Invalid item details");
      return;
    }

    setItems((prevItems) => [
      ...prevItems,
      {
        ...newItem,
        itemName: newItem.itemName || "Unnamed Item",
      },
    ]);
    setChangeLog((prev) => ({
      ...prev,
      addedItems: [...prev.addedItems, newItem],
    }));
    setPageError(null);
  };

  const handleDeleteItem = (index) => {
    const deletedItem = items[index];
    setItems((prevItems) => prevItems.filter((_, i) => i !== index));
    setChangeLog((prev) => ({
      ...prev,
      deletedItems: [...prev.deletedItems, deletedItem],
    }));
  };

  const calculateTotalAmount = () => {
    return items.reduce((acc, item) => acc + item.total, 0).toFixed(2);
  };

  const handleSaveDraft = async () => {
    if (!row?.IndentID) {
      setPageError("No indent selected");
      return;
    }

    if (items.length === 0) {
      setPageError("Please add items before saving draft");
      return;
    }

    try {
      await axios.put(
        `${API_BASE_URL}/viewTable/indents/${row.IndentID}/draft`,
        {
          isDraft: true,
          items: items.map((item) => ({
            itemID: item.itemID,
            quantity: item.quantity,
            remarks: item.remarks,
            categoryID: item.categoryID,
            stock: item.stock,
            promisedDate: item.promisedDate,
          })),
        }
      );

      navigate("/peindent", {
        state: {
          message: "Draft saved successfully",
        },
      });
    } catch (error) {
      setPageError("Failed to save draft. Please try again.");
      console.error("Draft saving error:", error);
    }
  };

  const handleSubmit = useCallback(async () => {
    if (!row?.IndentID) {
      setPageError("No indent selected");
      return;
    }

    if (items.length === 0) {
      setPageError("Please add items before submitting");
      return;
    }

    setSubmitting(true);
    setPageError(null);

    try {
      const submissionData = {
        indentId: row.IndentID,
        userId: currentUserId,
        items: items.map((item) => ({
          itemID: item.itemID,
          quantity: item.quantity,
          stock: item.stock,
          remarks: item.remarks || "",
          categoryID: item.categoryID,
          promisedDate: item.promisedDate,
          accountingUnitID: item.accountingUnitID || null,
        })),
      };

      const response = await axios.post(
        `${API_BASE_URL}/viewTable/indents/submit`,
        submissionData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          timeout: 30000, // 30 second timeout
        }
      );

      if (response.data && response.data.message) {
        setIsDraft(false);
        setIsEditable(false);

        navigate("/PEindent", {
          state: {
            message: response.data.message || "Indent submitted successfully",
          },
        });
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Submission failed. Please try again.";

      setPageError(errorMessage);
      console.error("Submission Error:", error.response?.data || error);
    } finally {
      setSubmitting(false);
    }
  }, [row, items, navigate, currentUserId]);

  const handlePrint = () => {
    window.print();
  };

  const handleRowClick = (row) => {
    setSelectedRow(row);
    setShowHistory(true);
  };

  const handleSearch = (query) => {
    // Implement search functionality
  };

  const handleDateFilter = (dateRange) => {
    // Implement date filter functionality
  };

  if (!row?.IndentID) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header
        exportEndpoint={apiEndpoint}
        importEndpoint={apiEndpoint}
        currentIndentData={pageData ? [pageData] : []}
        customFilename={`indent-${row?.IndentID}-${row?.ProjectName?.replace(/\s+/g, '-')}`}
      />

      <div className="flex-grow p-4">
        {isLoading && <LoadingOverlay message="Loading..." />}
        {pageError && <ErrorAlert message={pageError} />}
        
        {row && (
          <>
            <IndentDetails
              row={{
                ...pageData,
                IndentID: row.IndentID,
                ProjectName: row.ProjectName,
                TypeName: row.TypeName,
                Status: row.Status,
                CreationDate: row.CreationDate,
                UserName: row.UserName,
                TaskSchedule: row.TaskSchedule,
                L1: row.L1,
                L2: row.L2,
                PO: row.PO,
                PH: row.PH,
                IR: row.IR,
                IndentItems: pageData?.IndentItems || []
              }}
              onUpdate={(updatedData) => {
                setPageData(updatedData);
              }}
            />
            
          <ItemsTable
              indentID={row?.IndentID}
              indentType={row?.TypeName?.toLowerCase()}
              onAddItem={handleAddItem}
              onDeleteItem={handleDeleteItem}
              calculateTotalAmount={calculateTotalAmount}
            onUpdateItem={handleItemUpdate}
            isDraft={isDraft}
              isEditable={isEditable}
            onSubmit={handleSubmit}
            onSaveDraft={handleSaveDraft}
            onPrint={handlePrint}
            indentStatus={{
                l1: row?.L1 || 0,
                l2: row?.L2 || 0,
                po: row?.PO || 0,
                ph: row?.PH || 0,
                so: row?.IR || 0,
            }}
            changes={changeLog}
            storedChangeLogs={storedChangeLogs}
          />
        </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default InvoicingPage;